import cx from 'classnames';
import { CodeIcon } from '@heroicons/react/outline';
import {
  Box,
  Center,
  ChakraComponent,
  ChakraProps,
  Flex,
  Icon
} from '@chakra-ui/react';
import { CopyButton, ToggleKeysVisibility } from '@components/common';
import { onlyText } from 'react-children-utilities';
import { ComponentProps, ReactElement, useContext } from 'react';
import { KeyToggleContext } from '@context';
import { useInjectKeys } from './inject-keys';

type CodeBlockProps = {
  className?: string;
  children: ReactElement[];
  filename?: string;
  /**
   * Props passed to the wrapping container, by default the remaining `CodeBlock` props are spread to the `pre` element.
   */
  containerProps?: ComponentProps<ChakraComponent<'div'>>;
} & ChakraProps;

export const CodeBlock = ({
  className,
  children,
  filename,
  containerProps,
  ...rest
}: CodeBlockProps) => {
  const keyToggle = useContext(KeyToggleContext);
  const injectKeys = useInjectKeys();

  return (
    <Box pos="relative" my={6} {...containerProps}>
      <Box
        as="pre"
        bg="#282C34"
        border="1px solid var(--borderColor)"
        py={4}
        fontSize="sm"
        className={cx('rounded-md leading-[1.8]', className)}
        {...rest}
      >
        <Flex
          flexDirection="row"
          justifyContent={'space-between'}
          mb="4"
          mt="-4"
          px="5"
          py="2"
          textColor="gray.400"
          gap="2"
          borderBottom="1px solid #3F3F42"
        >
          <Box overflowX="auto">
            <Icon as={CodeIcon} boxSize={4} mr={3} mb="1px" />
            {filename && filename}
          </Box>

          <Flex direction="row" ml="auto" gap={2}>
            {keyToggle.isActive && (
              <ToggleKeysVisibility
                showKeys={keyToggle.showKeys}
                setShowKeys={keyToggle.toggleKeys}
              />
            )}
            <Center as="span">
              <CopyButton onCopyText={() => injectKeys(onlyText(children))} />
            </Center>
          </Flex>
        </Flex>
        <Box
          as="code"
          display="grid"
          overflowX="auto"
          color="#E1E4E8"
          // The below inline styles are required for line numbers to work. I don't think there's a better way to target the elements generated by our syntax highlighter.
          sx={{
            counterReset: 'line',
            '[data-line]': {
              px: 4
            },
            '[data-line]::before': {
              counterIncrement: 'line',
              content: 'counter(line)',
              display: 'inline-block',
              width: '1rem',
              marginRight: '1rem',
              textAlign: 'right',
              opacity: 0.5,
              color: 'gray.400'
            },
            "[data-line-numbers-max-digits='2'] > [data-line]::before": {
              width: '2rem'
            },
            "[data-line-numbers-max-digits='3'] > [data-line]::before": {
              width: '3rem'
            },
            '[data-highlighted-line]': {
              backgroundColor: '#c8c8ff1a'
            }
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export const PreBlock = ({ children, ...rest }: any) => {
  if ('type' in children && children['type'] === 'code') {
    return CodeBlock({ ...children['props'], ...rest });
  }
  return <pre {...rest}>{children}</pre>;
};
